import React, { useCallback } from 'react';
import {
  MenuItem,
  ListItemIcon,
  Checkbox,
  Tooltip,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { InfoIcon } from '../../../assets';
import { SelectOption } from '../../../types/globalTypes';
import { useStyles } from '../inputs/inputs.styles';

interface PackageTypeOption extends SelectOption {
  carrierCode?: string;
  carrierName?: string;
  description?: string;
}

interface FormikProps {
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
  values: Record<string, any>;
  touched?: Record<string, any>;
  errors?: Record<string, any>;
}

export interface PackageTypeSelectInputProps
  extends Omit<TextFieldProps, 'select' | 'children'> {
  options: PackageTypeOption[];
  formikProps?: Partial<FormikProps>;
  clearable?: boolean;
  useCustomValue?: boolean;
}

export function PackageTypeSelectInput({
  options,
  value,
  slotProps,
  formikProps,
  name,
  onChange,
  onBlur,
  style,
  useCustomValue,
  variant,
  ...otherProps
}: PackageTypeSelectInputProps): React.ReactElement {
  const { classes } = useStyles();

  const hasMultiple = (slotProps?.select as any)?.multiple;

  const {
    handleChange: formikHandleChange,
    handleBlur: formikHandleBlur,
    values,
    touched,
    errors,
  } = formikProps || {};

  const finalValue =
    formikProps !== undefined && !useCustomValue
      ? values?.[name as string]
      : value;
  const isTouched = touched?.[name as string];
  const errorText = errors?.[name as string] as string;

  const optionMap = options.reduce(
    (acc, option: PackageTypeOption) => {
      acc[option.id] = option.name as string;
      return acc;
    },
    {} as Record<string | number, string>,
  );

  const handleChange = (e: React.ChangeEvent<any>) => {
    onChange?.(e);
    !useCustomValue && formikHandleChange?.(e);
  };

  const handleBlur = (e: React.FocusEvent<any>) => {
    onBlur?.(e);
    !useCustomValue && formikHandleBlur?.(e);
  };

  const renderValue = useCallback(
    (selected: any) => {
      if (hasMultiple) {
        if (Array.isArray(selected)) {
          return selected.map((id) => optionMap[id]).join(', ');
        }
        return '';
      }

      return optionMap[selected] || '';
    },
    [hasMultiple, optionMap],
  );

  return (
    <TextField
      {...otherProps}
      name={name}
      select
      variant={variant || 'standard'}
      size='small'
      value={finalValue}
      onChange={handleChange}
      onBlur={handleBlur}
      error={isTouched && !!errorText}
      helperText={isTouched ? errorText : otherProps.helperText}
      slotProps={{
        ...slotProps,
        select: {
          MenuProps: {
            disableScrollLock: true,
            disablePortal: true,
          },
          multiple: hasMultiple,
          renderValue,
        },
        inputLabel: { className: classes.label },
      }}
      style={{ width: '100%', ...style }}
    >
      {options.map((option) =>
        getOptionWithTooltip({ option, hasMultiple, finalValue }),
      )}
    </TextField>
  );
}

const renderTooltip = (option: PackageTypeOption) => {
  if (option.id === 'all') {
    return null;
  }
  const parts: string[] = [];
  if (option.carrierName) {
    parts.push(`Carrier: ${option.carrierName}`);
  }
  if (option.description) {
    parts.push(`Description: ${option.description}`);
  }
  return parts.length > 0 ? parts.join('\n') : '';
};

interface OptionItemProps {
  option: PackageTypeOption;
  hasMultiple: boolean;
  finalValue: any;
}

const getOptionWithTooltip = ({
  option,
  hasMultiple,
  finalValue,
}: OptionItemProps) => {
  const tooltip = renderTooltip(option);

  const isChecked =
    hasMultiple && Array.isArray(finalValue) && finalValue.includes(option.id);

  return (
    <MenuItem key={option.id} value={option.id}>
      {hasMultiple && (
        <ListItemIcon>
          <Checkbox size='small' color='primary' checked={isChecked} />
        </ListItemIcon>
      )}

      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <span style={{ flex: 1 }}>{option.name}</span>

        {tooltip && (
          <Tooltip
            title={tooltip}
            placement='right'
            arrow
            slotProps={{
              tooltip: {
                style: { whiteSpace: 'pre-line', maxWidth: 300 },
              },
            }}
          >
            <InfoIcon
              color='secondary'
              style={{ fontSize: 16, marginLeft: 8, cursor: 'pointer' }}
            />
          </Tooltip>
        )}
      </div>
    </MenuItem>
  );
};
